import type { FinalConnectionState } from '@xyflow/react';
import { isWorkflowTriggerFlowNodeId } from './workflow-flow-node-ids';

export type ConnectEndResolution =
  | { action: 'add-node'; fromNodeId: string }
  | { action: 'connect'; fromNodeId: string; toNodeId: string }
  | { action: 'none' };

function elementUnderPointer(event: MouseEvent | TouchEvent): Element | null {
  if ('changedTouches' in event) {
    const touch = event.changedTouches[0];
    if (touch === undefined) {
      return null;
    }
    // touchend.target is the original handle, not the release point
    return document.elementFromPoint(touch.clientX, touch.clientY);
  }
  // mouseup.target is the element under the cursor
  if (event.target instanceof Element) {
    return event.target;
  }
  return null;
}

/**
 * Node under the pointer at connect-end.
 */
export function resolveDroppedOnFlowNodeId(
  event: MouseEvent | TouchEvent
): string | undefined {
  const el = elementUnderPointer(event);
  if (el === null) {
    return undefined;
  }
  const nodeEl = el.closest('.react-flow__node');
  if (!(nodeEl instanceof HTMLElement)) {
    return undefined;
  }
  const id = nodeEl.getAttribute('data-id');
  if (id === null || id === '') {
    return undefined;
  }
  return id;
}

/**
 * Decide what a finished connection drag should do:
 * - successful handle hit → nothing (onConnect already ran)
 * - drop on another node body → connect to that node
 * - drop on empty pane → open add-node from the source
 */
export function resolveConnectEnd(
  event: MouseEvent | TouchEvent,
  connectionState: FinalConnectionState,
  options: { isReconnecting: boolean }
): ConnectEndResolution {
  if (options.isReconnecting === true) {
    return { action: 'none' };
  }
  if (connectionState.isValid === true) {
    return { action: 'none' };
  }
  const fromNode = connectionState.fromNode;
  if (fromNode === null) {
    return { action: 'none' };
  }
  if (connectionState.fromHandle?.type !== 'source') {
    return { action: 'none' };
  }

  // Over a handle but rejected — don't open add-node or force-connect.
  if (connectionState.toHandle !== null) {
    return { action: 'none' };
  }

  const droppedOnNodeId = resolveDroppedOnFlowNodeId(event);
  if (droppedOnNodeId !== undefined) {
    if (droppedOnNodeId === fromNode.id) {
      return { action: 'none' };
    }
    return {
      action: 'connect',
      fromNodeId: fromNode.id,
      toNodeId: droppedOnNodeId,
    };
  }

  return { action: 'add-node', fromNodeId: fromNode.id };
}

/**
 * `connectFrom` for a node added by dropping a connection drag on the pane.
 *
 * A trigger's flow-node id (`trigger:<id>`) is NOT a workflow node — triggers
 * connect to entry nodes via synthetic edges. Storing a real edge from that id
 * makes the node a non-entry successor with an unknown source, which fails to
 * save ("Edge references an unknown node id") and corrupts entry-node
 * detection. So dragging from a trigger adds a fresh entry node (no explicit
 * edge); the trigger auto-connects to it. Dragging from a real node keeps the
 * linear chain by connecting from that node.
 */
export function connectFromForDroppedSource(
  fromNodeId: string
): readonly string[] | string {
  return isWorkflowTriggerFlowNodeId(fromNodeId) ? [] : fromNodeId;
}
